/**
 * 重度の破壊的操作の確認ダイアログ。
 *
 * 「メールアドレスの削除」「ドメインの削除」のように **保存済みのメールが消える**
 * 操作は、`<ConfirmDialog>` のワンクリック確認では軽すぎる。
 * 対象の名前を手で入力させて、誤操作を構造的に防ぐ（05-ui.md §8 設計原則 4）。
 */
import * as React from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
} from '@mail-sv/ui';
import { errorMessage } from '../lib/api.js';

export function ConfirmTextDialog({
  open,
  onOpenChange,
  title,
  description,
  /** 入力させる文字列。通常は削除対象の名前そのもの。 */
  confirmText,
  /** 消えるもの・止まるものを箇条書きで明示する。 */
  consequences,
  confirmLabel = '削除する',
  pending = false,
  error,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmText: string;
  consequences: readonly string[];
  confirmLabel?: string;
  pending?: boolean;
  error?: unknown;
  onConfirm: () => void;
}) {
  const [input, setInput] = React.useState('');
  const inputId = React.useId();

  // 開き直したときに前回の入力が残っていると、勢いでそのまま押せてしまう。
  React.useEffect(() => {
    if (open) setInput('');
  }, [open]);

  const matched = input.trim() === confirmText;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // 送信中は閉じさせない（§6 フォーム規約）。
        if (pending) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        <Alert variant="destructive">
          <AlertTitle>この操作は取り消せません</AlertTitle>
          <AlertDescription>
            <ul className="list-disc space-y-1 pl-4">
              {consequences.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>

        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage(error)}</AlertDescription>
          </Alert>
        ) : null}

        <Field
          label={
            <>
              確認のため <code className="font-mono text-foreground">{confirmText}</code> と入力してください
            </>
          }
          htmlFor={inputId}
        >
          <Input
            id={inputId}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoComplete="off"
            placeholder={confirmText}
          />
        </Field>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            キャンセル
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!matched}
            loading={pending}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
